import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Loader2, Plus } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useClaims, type Claim } from '@/api/claims';
import { useInvoices, type Invoice, type InvoiceStatus } from '@/api/invoices';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/Badge';
import { Alert } from '@/components/ui/Alert';
import { cn } from '@/lib/cn';
import { CreateInvoiceDialog } from './CreateInvoiceDialog';

/*
 * Invoice tab — two sub-tabs:
 *
 *   Unbilled : client-billed claims (bill_to='Client') in status=Ready that
 *              aren't already on a non-voided invoice. Biller multi-selects
 *              from a single client and clicks "Create invoice from N claims"
 *              to open CreateInvoiceDialog.
 *
 *   Invoices : issued invoices with filter chips by status and drill-through
 *              to the detail page.
 *
 * Private-pay claims start their life on the Unbilled sub-tab and graduate
 * to Invoices once the biller bundles them. Phase 4f excluded them from the
 * Claims tab.
 */

type SubTab = 'unbilled' | 'invoices';
const INVOICE_STATUSES: InvoiceStatus[] = ['Issued', 'Partial', 'Paid', 'Voided'];

function fmtMoney(n: number): string {
    return (n || 0).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtDate(iso: string | null): string {
    if (!iso) return '';
    return String(iso).slice(0, 10);
}

export function InvoicesTab() {
    const canEdit = useAuthStore((s) => s.hasPermission('edit_billing'));
    const [sub, setSub] = useState<SubTab>('unbilled');

    return (
        <>
            <div
                role="tablist"
                aria-label="Invoice sub-sections"
                className="flex items-center gap-1 mb-3 p-0.5 bg-surface-muted rounded-md w-fit"
            >
                <SubTabButton active={sub === 'unbilled'}   onClick={() => setSub('unbilled')}>Unbilled</SubTabButton>
                <SubTabButton active={sub === 'invoices'}   onClick={() => setSub('invoices')}>Invoices</SubTabButton>
            </div>

            {sub === 'unbilled' ? <UnbilledPanel canEdit={canEdit} /> : <InvoicesPanel />}
        </>
    );
}

function SubTabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            onClick={onClick}
            className={cn(
                'px-3 py-1 text-xs rounded transition-colors',
                active
                    ? 'bg-surface text-text shadow-xs font-medium'
                    : 'text-text-muted hover:text-text',
            )}
        >
            {children}
        </button>
    );
}

// ── Unbilled sub-tab ─────────────────────────────────────────────

function UnbilledPanel({ canEdit }: { canEdit: boolean }) {
    const { data, isLoading, isError } = useClaims({ billTo: 'Client', invoiced: false });
    // Load Payer claims alongside so PR-spawn rows can surface their source
    // claim #. This is a cached query TanStack already warms elsewhere; cold
    // loads just fall back to a blank badge without blocking render.
    const { data: payerClaims } = useClaims({ billTo: 'Payer' });
    const payerById = useMemo(() => {
        const map = new Map<string, Claim>();
        for (const c of (payerClaims ?? [])) map.set(c.id, c);
        return map;
    }, [payerClaims]);

    const rows = useMemo(() => (data ?? []).filter((c) => c.status === 'Ready'), [data]);

    // Group by client so the biller can confidently multi-select without the
    // dialog erroring out at submit time. A mixed selection is never valid,
    // so we gate the action button on a single-client selection.
    const byClient = useMemo(() => {
        const map = new Map<string, Claim[]>();
        for (const c of rows) {
            const key = c.clientId || '—';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(c);
        }
        return map;
    }, [rows]);

    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [dialogOpen, setDialogOpen] = useState(false);

    // Clear selection whenever the underlying row set changes — avoids the
    // stale-selection footgun if a row gets pulled onto an invoice in another
    // tab and this view refetches.
    useEffect(() => {
        setSelected((prev) => {
            const alive = new Set(rows.map((r) => r.id));
            const next = new Set<string>();
            for (const id of prev) if (alive.has(id)) next.add(id);
            return next.size === prev.size ? prev : next;
        });
    }, [rows]);

    const selectedRows = useMemo(
        () => rows.filter((r) => selected.has(r.id)),
        [rows, selected],
    );
    const selectedClientIds = new Set(selectedRows.map((r) => r.clientId).filter(Boolean));
    const mixedClients = selectedClientIds.size > 1;
    const selectedClientId = selectedClientIds.size === 1 ? [...selectedClientIds][0] : null;
    const selectedClientName = selectedRows[0]?.client || '';
    const selectedTotal = selectedRows.reduce((s, r) => s + r.amount, 0);

    function toggleRow(id: string, checked: boolean) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }

    if (isLoading) {
        return (
            <Card className="p-10 flex items-center justify-center text-text-muted">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading unbilled claims…
            </Card>
        );
    }
    if (isError) {
        return <Alert variant="danger">Failed to load claims.</Alert>;
    }

    return (
        <>
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-text-subtle">
                    Claims billed directly to the client, ready to bundle onto an invoice. Select claims from a single client.
                </p>
                {canEdit && (
                    <div className="flex items-center gap-3">
                        {mixedClients && (
                            <span className="text-xs text-warning-text">
                                Mixed clients — pick one client at a time
                            </span>
                        )}
                        <Button
                            variant="primary"
                            size="sm"
                            disabled={selected.size === 0 || mixedClients}
                            onClick={() => setDialogOpen(true)}
                        >
                            <Plus className="w-3.5 h-3.5" />
                            Create invoice{selected.size > 0 ? ` (${selected.size})` : ''}
                        </Button>
                    </div>
                )}
            </div>

            <Card className="p-0 overflow-hidden">
                {rows.length === 0 ? (
                    <div className="p-10 text-center text-sm text-text-muted">
                        No unbilled private-pay claims. Claims created with bill-to=Client in Ready status appear here.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-surface-muted text-text-subtle uppercase tracking-wider text-2xs">
                                <tr>
                                    <th className="px-3 py-2 w-8" aria-label="Select"></th>
                                    <th className="px-3 py-2 text-left font-bold">Claim #</th>
                                    <th className="px-3 py-2 text-left font-bold">Client</th>
                                    <th className="px-3 py-2 text-left font-bold">DOS</th>
                                    <th className="px-3 py-2 text-left font-bold">Code</th>
                                    <th className="px-3 py-2 text-right font-bold">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...byClient.entries()].map(([, group]) => (
                                    group.map((c, i) => (
                                        <tr
                                            key={c.id}
                                            className={cn(
                                                'border-t border-border hover:bg-surface-muted/50',
                                                i === 0 && 'border-t-2',
                                                selected.has(c.id) && 'bg-accent/5',
                                            )}
                                        >
                                            <td className="px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select ${c.claimNo}`}
                                                    checked={selected.has(c.id)}
                                                    onChange={(e) => toggleRow(c.id, e.target.checked)}
                                                    disabled={!canEdit}
                                                    className="w-3.5 h-3.5 accent-accent"
                                                />
                                            </td>
                                            <td className="px-3 py-2 font-mono text-text">
                                                <Link to={`/billing/${c.id}`} className="hover:text-accent-text">{c.claimNo}</Link>
                                                {c.prSourceClaimId && (
                                                    <span
                                                        className="ml-2 inline-flex items-center px-1.5 py-0.5 text-2xs rounded bg-accent/10 text-accent-text font-sans"
                                                        title="Patient responsibility spawned from payer claim"
                                                    >
                                                        PR from {payerById.get(c.prSourceClaimId)?.claimNo ?? '—'}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-text">{c.client}</td>
                                            <td className="px-3 py-2 text-text-muted tabular-nums">{fmtDate(c.serviceDate)}</td>
                                            <td className="px-3 py-2 text-text-muted">{c.serviceCode}</td>
                                            <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(c.amount)}</td>
                                        </tr>
                                    ))
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>

            {selectedClientId && canEdit && (
                <CreateInvoiceDialog
                    open={dialogOpen}
                    onOpenChange={setDialogOpen}
                    clientId={selectedClientId}
                    clientName={selectedClientName}
                    claims={selectedRows}
                    subtotal={selectedTotal}
                    onSuccess={() => { setSelected(new Set()); setDialogOpen(false); }}
                />
            )}
        </>
    );
}

// ── Invoices sub-tab ─────────────────────────────────────────────

function InvoicesPanel() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState<InvoiceStatus | 'All'>('Issued');
    const { data, isLoading, isError } = useInvoices();

    const rows = useMemo(() => {
        if (!data) return [] as Invoice[];
        if (filter === 'All') return data;
        return data.filter((i) => i.status === filter);
    }, [data, filter]);

    if (isLoading) {
        return (
            <Card className="p-10 flex items-center justify-center text-text-muted">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading invoices…
            </Card>
        );
    }
    if (isError) {
        return <Alert variant="danger">Failed to load invoices.</Alert>;
    }

    return (
        <>
            <div className="flex items-center gap-2 mb-3">
                <FilterChip active={filter === 'All'} onClick={() => setFilter('All')}>All</FilterChip>
                {INVOICE_STATUSES.map((s) => (
                    <FilterChip key={s} active={filter === s} onClick={() => setFilter(s)}>{s}</FilterChip>
                ))}
            </div>

            <Card className="p-0 overflow-hidden">
                {rows.length === 0 ? (
                    <div className="p-10 text-center text-sm text-text-muted">
                        No invoices {filter !== 'All' ? `in ${filter}` : 'yet'}.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-surface-muted text-text-subtle uppercase tracking-wider text-2xs">
                                <tr>
                                    <th className="px-3 py-2 text-left font-bold">Invoice #</th>
                                    <th className="px-3 py-2 text-left font-bold">Client</th>
                                    <th className="px-3 py-2 text-left font-bold">Issued</th>
                                    <th className="px-3 py-2 text-left font-bold">Due</th>
                                    <th className="px-3 py-2 text-right font-bold">Subtotal</th>
                                    <th className="px-3 py-2 text-right font-bold">Paid</th>
                                    <th className="px-3 py-2 text-right font-bold">Balance</th>
                                    <th className="px-3 py-2 text-left font-bold">Status</th>
                                    <th className="px-3 py-2 text-left font-bold">Delivered</th>
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((inv) => {
                                    const href = `/billing/invoices/${inv.id}`;
                                    return (
                                    <tr
                                        key={inv.id}
                                        onClick={(e) => {
                                            // Don't double-navigate when the user clicked the inner
                                            // Link (browser handles it) or any interactive child.
                                            if ((e.target as HTMLElement).closest('a, button, input, label, [role="checkbox"]')) return;
                                            navigate(href);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && e.target === e.currentTarget) {
                                                navigate(href);
                                            }
                                        }}
                                        tabIndex={0}
                                        className="border-t border-border hover:bg-surface-muted/50 cursor-pointer focus:outline-2 focus:outline-accent focus:outline-offset-[-2px]"
                                    >
                                        <td className="px-3 py-2 font-mono text-text">
                                            <Link to={href} className="hover:text-accent-text">{inv.displayId}</Link>
                                        </td>
                                        <td className="px-3 py-2 text-text">{inv.clientName}</td>
                                        <td className="px-3 py-2 text-text-muted tabular-nums">{fmtDate(inv.issuedAt)}</td>
                                        <td className="px-3 py-2 text-text-muted tabular-nums">{fmtDate(inv.dueDate)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(inv.subtotal)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(inv.paidAmount)}</td>
                                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(inv.balance)}</td>
                                        <td className="px-3 py-2"><StatusBadge status={inv.status} /></td>
                                        <td className="px-3 py-2 text-text-muted">
                                            {inv.deliveryMethod ? `${inv.deliveryMethod} · ${fmtDate(inv.deliveredAt)}` : '—'}
                                        </td>
                                    </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </>
    );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'px-3 py-1 text-xs rounded-full border transition-colors',
                active
                    ? 'bg-accent/10 border-accent text-accent-text'
                    : 'bg-surface border-border text-text-muted hover:text-text',
            )}
        >
            {children}
        </button>
    );
}
